import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { Rss } from "lucide-react";
import type { ChangelogBody, ChangelogEntryView } from "../shared/types";
import { CHANGELOG_RSS } from "../shared/paths";
import { CodeBlock } from "../components/code";
import { Callout } from "../components/blocks";
import { DocsLink } from "../components/DocsLink";
import { Media } from "../components/Media";
import { COPY } from "../copy";

/**
 * The Changelog (design §5): one entry per change, newest first, anchored by date + title; the tag
 * filter on the right; the RSS feed linked. A task page shows its own entries with the same
 * component.
 */

const copyAnchor = (id: string) => {
  navigator.clipboard?.writeText(`${window.location.origin}${window.location.pathname}#${id}`);
  window.history.replaceState(null, "", `#${id}`);
};

const Entry = ({ entry, tagTitles }: { entry: ChangelogEntryView; tagTitles: Record<string, string> }) => (
  <article id={entry.id} className="scroll-mt-24 border-b border-border py-8 clg:grid clg:grid-cols-[10rem_minmax(0,1fr)] clg:gap-6">
    <div>
      <button type="button" onClick={() => copyAnchor(entry.id)} className="text-bodySmall font-semibold text-foreground" aria-label={COPY.copyLink}>
        {entry.dateLabel}
      </button>
      <ul className="mt-2 flex flex-wrap gap-1">
        {entry.tags.map((tag) => (
          <li key={tag} className="rounded bg-muted px-1.5 py-0.5 text-headline2 text-muted-foreground">
            {tagTitles[tag] ?? tag}
          </li>
        ))}
      </ul>
    </div>
    <div className="min-w-0">
      <h2 className="text-head5 font-semibold text-foreground">{entry.title}</h2>
      {entry.tags.includes("breaking_change") ? <Callout role="warning">{entry.compatibility}</Callout> : null}
      <p className="mt-2 text-bodyMedium text-foreground">{entry.benefit}</p>
      {entry.bullets.length ? (
        <ul className="mt-2 list-disc space-y-1 pl-6 text-bodyMedium">
          {entry.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      ) : null}
      {entry.code ? <CodeBlock sample={entry.code} /> : null}
      {entry.frame ? <Media file={entry.frame} alt={entry.title} /> : null}
      {entry.removal_notice ? (
        <div className="mt-3 text-bodySmall">
          <p className="font-semibold">
            {COPY.removal}: {entry.removal_notice.effective_date}
          </p>
          <dl className="mt-1 space-y-1">
            {(
              [
                [COPY.removalHowToIdentify, entry.removal_notice.how_to_identify],
                [COPY.removalIfAlready, entry.removal_notice.if_already],
                [COPY.removalToKeepWorking, entry.removal_notice.to_keep_working],
                [COPY.removalWhatStays, entry.removal_notice.what_stays],
              ] as const
            ).map(([label, text]) => (
              <div key={label}>
                <dt className="inline font-semibold">{label}: </dt>
                <dd className="inline">{text}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
      {!entry.tags.includes("breaking_change") && entry.compatibility ? <p className="mt-3 text-bodySmall text-muted-foreground">{entry.compatibility}</p> : null}
      {entry.link ? (
        <DocsLink href={entry.link} className="mt-2 inline-block text-bodySmall text-purpleGreen underline">
          {COPY.readMore}
        </DocsLink>
      ) : null}
    </div>
  </article>
);

export const ChangelogEntries = ({ entries, tagTitles = {} }: { entries: ChangelogEntryView[]; tagTitles?: Record<string, string> }) => (
  <div>
    {entries.map((entry) => (
      <Entry key={entry.id} entry={entry} tagTitles={tagTitles} />
    ))}
  </div>
);

export const ChangelogPage = ({ body }: { body: ChangelogBody }) => {
  const [tag, setTag] = useState<string | null>(null);
  const tagTitles = useMemo(() => Object.fromEntries(body.tags.map((t) => [t.value, t.title])), [body.tags]);
  const used = body.tags.filter((t) => body.entries.some((e) => e.tags.includes(t.value)));
  const shown = tag ? body.entries.filter((e) => e.tags.includes(tag)) : body.entries;

  return (
    <div className="clg:grid clg:grid-cols-[minmax(0,1fr)_12rem] clg:gap-10">
      <ChangelogEntries entries={shown} tagTitles={tagTitles} />
      <aside className="order-first mb-6 clg:order-none clg:mb-0">
        <div className="sticky top-32 space-y-3">
          <p className="text-bodySmall font-semibold text-foreground">{COPY.filterByTag}</p>
          <div className="flex flex-wrap gap-1 clg:flex-col">
            {[{ value: null, title: COPY.allTags }, ...used].map((t) => (
              <button
                key={t.title}
                type="button"
                onClick={() => setTag(t.value)}
                className={clsx("rounded-full px-2.5 py-1 text-left text-bodySmall", tag === t.value ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground")}
              >
                {t.title}
              </button>
            ))}
          </div>
          <a href={CHANGELOG_RSS} className="flex items-center gap-1 text-bodySmall text-muted-foreground hover:text-foreground">
            <Rss size={14} />
            {COPY.rss}
          </a>
        </div>
      </aside>
    </div>
  );
};
